import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import PageContainer from "../components/containers/PageContainer";
import {
  Search as SearchIcon,
  Calendar,
  Filter,
  Download,
  AlertCircle,
} from "lucide-react";
import { TailSpin } from "react-loader-spinner";
import Table from "../components/table/Table";
import MultiSelectDropDown from "../components/nav/MultiSelectDropDown";
import useDeepSearch from "../hooks/useDeepSearch";

// Main container for the search page
const SearchPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
  padding: 50px 10px 10px 10px;
`;

// Header section with title and description
const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h1`
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 32px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  margin: 0;
  letter-spacing: 0.32px;
`;

const Description = styled.p`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin: 0;
  max-width: 600px;
`;

// Big search bar section
const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
`;

const BigSearchBar = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #ebebeb;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  height: 60px;
  position: relative;
  background: #fff;
  box-shadow: 0px 2px 20px 8px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s ease-in-out;

  &:focus-within {
    border-color: #ff734f;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 20px 24px;
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  background: transparent;

  &::placeholder {
    color: #828283;
  }
`;

const SearchButton = styled.button`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  border-radius: 0 10px 10px 0;
  background: #ff734f;
  width: 70px;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: #e5633d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Filter bar section
const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 25px;
  border: 1px solid #ebebeb;
  border-radius: 18px;
  background: #fcfcfc;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterTitle = styled.h3`
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin: 0;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  width: 100%;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
  color: #3c3c3d;
  background: #fff;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #ff734f;
  }

  &::placeholder {
    color: #828283;
  }
`;

const DateInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 12px;
  color: #828283;
  pointer-events: none;
`;

// Results section
const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ResultsCount = styled.span`
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const ResultsActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ff734f;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  color: #fff;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #e5633d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #ff734f;
`;

const HeaderCheckbox = styled(Checkbox)`
  margin: 0;
`;

const ClearFiltersButton = styled.button`
  background: none;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 8px 16px;
  color: #6e6e6e;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #ff734f;
    color: #ff734f;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c33;
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

// Add new styled components for tooltip and clickable description
const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const DescriptionText = styled.span<{ clickable?: boolean }>`
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  color: ${(props) => (props.clickable ? "#ff734f" : "inherit")};
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${(props) => (props.clickable ? "#e5633d" : "inherit")};
    text-decoration: ${(props) => (props.clickable ? "underline" : "none")};
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 300px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333;
  }

  ${TooltipContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

// Helper function to truncate long text
const truncateText = (text: string, maxLength: number = 150): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// Custom component for description with tooltip and click functionality
const DescriptionWithTooltip: React.FC<{
  description: string;
  type: string;
  id: string;
  onAmendmentClick: (id: string) => void;
}> = ({ description, type, id, onAmendmentClick }) => {
  const truncatedText = truncateText(description);
  const isClickable = type === "amendment";

  const handleClick = () => {
    if (isClickable) {
      onAmendmentClick(id);
    }
  };

  return (
    <TooltipContainer>
      <DescriptionText
        clickable={isClickable}
        onClick={handleClick}
        title={isClickable ? "Cliquer pour voir l'amendement" : undefined}
      >
        {truncatedText}
      </DescriptionText>
      {description.length > 150 && <Tooltip>{description}</Tooltip>}
    </TooltipContainer>
  );
};

interface SearchResult {
  id: string;
  text: string;
  date: string;
  deputy: string;
  description: string;
  group: string;
  chamber: string;
  type: string; // Add the type field
}

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Filter states
  const [textFilter, setTextFilter] = useState<string>("");
  const [dateFromFilter, setDateFromFilter] = useState<string>("");
  const [dateToFilter, setDateToFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<
    { label: string; value: string }[]
  >([]);
  const [chamberFilter, setChamberFilter] = useState<
    { label: string; value: string }[]
  >([]);

  // Use the deep search hook
  const {
    data: searchResults,
    loading: isSearching,
    error: searchError,
    search,
    reset,
  } = useDeepSearch();

  // Updated mock data for filters based on actual API response
  const groupOptions = [
    { label: "La République En Marche", value: "EPR" },
    { label: "Renaissance", value: "RE" },
    { label: "Les Républicains", value: "LR" },
    { label: "Rassemblement National", value: "RN" },
    {
      label: "La France Insoumise - Nouveau Front Populaire",
      value: "LFI-NFP",
    },
    { label: "La France Insoumise - NUPES", value: "LFI - NUPES" },
    { label: "Gauche Démocrate et Républicaine", value: "GDR" },
    { label: "Gauche Démocrate et Républicaine - NUPES", value: "GDR - NUPES" },
    {
      label: "Libertés, Indépendants, Outre-mer et Territoires",
      value: "LIOT",
    },
    { label: "Parti Socialiste", value: "PS" },
    { label: "Écologiste - NUPES", value: "ECO - NUPES" },
    { label: "Horizons et apparentés", value: "HOR" },
    { label: "UDI et Indépendants", value: "UDI" },
    { label: "Non inscrit", value: "NI" },
  ];

  const chamberOptions = [
    { label: "Assemblée Nationale", value: "AN" },
    { label: "Sénat", value: "SEN" },
  ];

  // Mapping functions for display purposes
  const getGroupDisplayName = (groupCode: string): string => {
    const group = groupOptions.find((g) => g.value === groupCode);
    return group ? group.label : groupCode;
  };

  const getChamberDisplayName = (chamberCode: string): string => {
    const chamber = chamberOptions.find((c) => c.value === chamberCode);
    return chamber ? chamber.label : chamberCode;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    // Prepare search parameters
    const searchParams = {
      words: searchQuery,
      beginAt: dateFromFilter || undefined,
      endAt: dateToFilter || undefined,
      group:
        groupFilter.length > 0 ? groupFilter.map((g) => g.value) : undefined,
      chamber:
        chamberFilter.length > 0
          ? chamberFilter.map((c) => c.value)
          : undefined,
    };

    await search(searchParams);
  };

  const handleRowSelect = (id: string, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(id);
    } else {
      newSelectedRows.delete(id);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredResults.length > 0) {
      const allIds = new Set(filteredResults.map((result) => result.id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleExport = () => {
    const selectedData = filteredResults.filter((result) =>
      selectedRows.has(result.id)
    );

    // Simple CSV export functionality
    const headers = [
      "Texte",
      "Date",
      "Député",
      "Description",
      "Groupe",
      "Chambre",
    ];
    const csvContent = [
      headers.join(","),
      ...selectedData.map((row) =>
        [
          `"${row.text}"`,
          row.date,
          `"${row.deputy}"`,
          `"${row.description.replace(/"/g, '""')}"`, // Escape quotes in description
          `"${getGroupDisplayName(row.group)}"`,
          `"${getChamberDisplayName(row.chamber)}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `search-results-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClearFilters = () => {
    setTextFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setGroupFilter([]);
    setChamberFilter([]);
    setSelectedRows(new Set());
    reset(); // Clear search results
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Navigation handler for amendments
  const handleAmendmentClick = (amendmentId: string) => {
    // For now, we'll navigate to the general amendment route
    // In a real scenario, you might want to determine the correct route based on context
    // Since we don't have deputy or file context, we can navigate to a general amendment page
    // or create a dedicated amendment detail page that doesn't require those contexts

    // Option 1: Navigate to a hypothetical general amendment route
    navigate(`/amendment/${amendmentId}`);

    // Option 2: If you prefer to use existing routes, you might need additional context
    // For example, if you have deputy info: navigate(`/people/${deputyId}/amendments/${amendmentId}`);
    // Or if you have file info: navigate(`/file/${fileId}/amendts/${amendmentId}`);
  };

  // Use search results from the API, fallback to empty array if no results
  const results = searchResults || [];

  // Filter results based on current filters (client-side filtering)
  const filteredResults = results.filter((result) => {
    const matchesText =
      !textFilter ||
      result.text.toLowerCase().includes(textFilter.toLowerCase()) ||
      result.description.toLowerCase().includes(textFilter.toLowerCase());

    const matchesGroup =
      groupFilter.length === 0 ||
      groupFilter.some((group) => result.group === group.value);

    const matchesChamber =
      chamberFilter.length === 0 ||
      chamberFilter.some((chamber) => result.chamber === chamber.value);

    const matchesDateFrom = !dateFromFilter || result.date >= dateFromFilter;
    const matchesDateTo = !dateToFilter || result.date <= dateToFilter;

    return (
      matchesText &&
      matchesGroup &&
      matchesChamber &&
      matchesDateFrom &&
      matchesDateTo
    );
  });

  // Clear selected rows when filtered results change
  useEffect(() => {
    const validSelectedRows = new Set(
      Array.from(selectedRows).filter((id) =>
        filteredResults.some((result) => result.id === id)
      )
    );
    if (validSelectedRows.size !== selectedRows.size) {
      setSelectedRows(validSelectedRows);
    }
  }, [filteredResults, selectedRows]);

  const isAllSelected =
    filteredResults.length > 0 &&
    filteredResults.every((result) => selectedRows.has(result.id));
  const isIndeterminate = selectedRows.size > 0 && !isAllSelected;

  const tableColumns = [
    "",
    "Texte",
    "Date",
    "Député",
    "Description",
    "Groupe",
    "Chambre",
  ];

  const tableData = filteredResults.map((result) => ({
    "": (
      <CheckboxCell>
        <Checkbox
          type="checkbox"
          checked={selectedRows.has(result.id)}
          onChange={(e) => handleRowSelect(result.id, e.target.checked)}
        />
      </CheckboxCell>
    ),
    Texte: result.text,
    Date: new Date(result.date).toLocaleDateString("fr-FR"),
    Député: result.deputy,
    Description: (
      <DescriptionWithTooltip
        description={result.description}
        type={result.type}
        id={result.id}
        onAmendmentClick={handleAmendmentClick}
      />
    ),
    Groupe: getGroupDisplayName(result.group),
    Chambre: getChamberDisplayName(result.chamber),
  }));

  const customRenderers = {
    "": (value: any) => value, // Return the checkbox component as-is
  };

  // Custom header for the checkbox column
  const customHeaders = {
    "": (
      <CheckboxCell>
        <HeaderCheckbox
          type="checkbox"
          checked={isAllSelected}
          ref={(input) => {
            if (input) input.indeterminate = isIndeterminate;
          }}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      </CheckboxCell>
    ),
  };

  return (
    <PageContainer>
      <SearchPageContainer>
        {/*never set a header */}
        <SearchSection>
          <BigSearchBar>
            <SearchInput
              type="text"
              placeholder="Recherchez des documents, amendements, discours..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <SearchButton
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? (
                <TailSpin
                  visible={true}
                  height="24"
                  width="24"
                  color="#ffffff"
                  ariaLabel="tail-spin-loading"
                  radius="1"
                />
              ) : (
                <SearchIcon size={24} color="#fff" />
              )}
            </SearchButton>
          </BigSearchBar>

          {searchError && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {searchError}
            </ErrorMessage>
          )}
        </SearchSection>

        <FilterSection>
          {/*never set a header */}
          <FilterGrid>
            <FilterField>
              <FilterLabel>Texte</FilterLabel>
              <FilterInput
                type="text"
                placeholder="Filtrer par contenu..."
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
              />
            </FilterField>

            <FilterField>
              <FilterLabel>Date de début</FilterLabel>
              <DateInputContainer>
                <FilterInput
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                />
              </DateInputContainer>
            </FilterField>

            <FilterField>
              <FilterLabel>Date de fin</FilterLabel>
              <DateInputContainer>
                <FilterInput
                  type="date"
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                />
              </DateInputContainer>
            </FilterField>

            <FilterField>
              <FilterLabel>Groupe politique</FilterLabel>
              <MultiSelectDropDown
                items={groupOptions}
                onSelect={setGroupFilter}
                placeholder="Sélectionner des groupes..."
                selected={groupFilter}
                multiChoice={true}
              />
            </FilterField>

            <FilterField>
              <FilterLabel>Chambre</FilterLabel>
              <MultiSelectDropDown
                items={chamberOptions}
                onSelect={setChamberFilter}
                placeholder="Sélectionner des chambres..."
                selected={chamberFilter}
                multiChoice={true}
              />
            </FilterField>
          </FilterGrid>
        </FilterSection>

        <ResultsSection>
          <ResultsHeader>
            <ResultsCount>
              {searchResults ? (
                <>
                  {filteredResults.length} résultat
                  {filteredResults.length !== 1 ? "s" : ""} trouvé
                  {filteredResults.length !== 1 ? "s" : ""}
                  {selectedRows.size > 0 && (
                    <span>
                      {" "}
                      ({selectedRows.size} sélectionné
                      {selectedRows.size !== 1 ? "s" : ""})
                    </span>
                  )}
                </>
              ) : (
                "Utilisez la barre de recherche pour commencer"
              )}
            </ResultsCount>
            <ResultsActions>
              {selectedRows.size > 0 && (
                <ExportButton onClick={handleExport}>
                  <Download size={14} />
                  Exporter
                </ExportButton>
              )}
              <ClearFiltersButton onClick={handleClearFilters}>
                Effacer les filtres
              </ClearFiltersButton>
            </ResultsActions>
          </ResultsHeader>

          <Table
            columns={tableColumns}
            data={tableData}
            customRenderers={customRenderers}
            customHeaders={customHeaders}
            isLoading={isSearching}
            emptyDateMessage={
              searchResults === null
                ? "Lancez une recherche pour voir les résultats."
                : "Aucun résultat trouvé. Essayez de modifier vos critères de recherche."
            }
          />
        </ResultsSection>
      </SearchPageContainer>
    </PageContainer>
  );
};

export default Search;
